import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import EmptyState from '../../../commodity/components/ui/EmptyState';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import type { DisplayTimeZone } from '../api/learner';
import { getHouseholdCalendar, HouseholdCalendarEvent } from '../api/schedule';
import { getStudents } from '../api/students';
import LearnerFilter, {
  ALL_LEARNERS,
  LearnerOption,
  useHouseholdFilter,
} from '../components/LearnerFilter';
import { joinStateAt } from '../learner/joinWindow';
import { useServerClock } from '../learner/learnerTime';
import {
  addDays,
  addMonths,
  dayKey,
  familyZoneLabel,
  formatDayAndTime,
  formatDayKey,
  formatMonthKey,
  formatTime,
  monthWeeks,
  weekDays,
} from '../time/zonedTime';
import { CALENDAR_EVENT_STATE_LABEL } from '../vocabulary/learningVocabulary';

type View = 'week' | 'month';

/** The calendar as the server last answered it. */
interface Answer {
  tz: DisplayTimeZone;
  serverNow: string;
  events: HouseholdCalendarEvent[];
  /** Some enrollment could not be read: the calendar is partial. */
  incomplete: boolean;
  incompleteLearners: string[];
}

/**
 * Router state a past-class link carries, so the history opens on the
 * event's learner — without the learner id in the address (AC-11, AC-25).
 */
export interface PastClassesLinkState {
  learnerId: string;
}

const CLOCK_TICK_MS = 30_000;

/** The days a view shows around `anchor`. */
const daysOf = (view: View, anchor: string): string[] =>
  view === 'week' ? weekDays(anchor) : monthWeeks(anchor).flat();

/**
 * The instants to ask for. A local day lies within a day either side of the
 * same UTC date in every zone, so asking a day wider each way and placing
 * events by their day in the display zone needs no offset arithmetic here.
 */
const rangeOf = (days: string[]) => ({
  from: `${addDays(days[0], -1)}T00:00:00.000Z`,
  to: `${addDays(days[days.length - 1], 2)}T00:00:00.000Z`,
});

const weekLabel = (days: string[]) =>
  `${formatDayKey(days[0], { day: 'numeric', month: 'short' })} – ${formatDayKey(days[6], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;

/**
 * UC-P08 "Agenda" — the household's calendar (PRT-14-FR-01..07).
 *
 * Every event is a booking the server returned: nothing is repeated, filled
 * in or computed from a pattern (AC-05). Times are drawn in the zone the
 * server chose, labelled, and "today" is the server's today: the device clock
 * only measures how long ago the server answered. Moving between weeks and
 * months, or between learners, keeps the other choice (AC-10).
 *
 * An event leads to the class door while the server's window is open — the
 * session room re-checks entry itself (AC-20) — to past classes once it has
 * happened, and otherwise to its enrollment. A moved class stays at its
 * current time with the proposal beside it; a cancelled one stays, marked
 * cancelled, with no door (FR-07).
 */
const ParentSchedule: React.FC = () => {
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState<string | null>(null);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [students, setStudents] = useState<LearnerOption[]>([]);
  const [filter, setFilter] = useHouseholdFilter();

  const now = useServerClock(answer?.serverNow, CLOCK_TICK_MS);

  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => getStudents())
      .then((list) => {
        if (live) {
          setStudents(
            (list ?? []).map((s) => ({
              studentId: s.studentId,
              name: `${s.firstName} ${s.lastName}`.trim(),
            })),
          );
        }
      })
      // The learners in the events stand in for the list when it cannot be read.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      if (!anchor) {
        // First the server's clock and zone, so the first week shown is the
        // server's this week in the household's zone. Setting the anchor
        // loads that week.
        const probe = new Date().toISOString();
        const first = await getHouseholdCalendar(probe, probe);
        setAnchor(dayKey(first.serverNow, first.timeZone));
        return;
      }
      const { from, to } = rangeOf(daysOf(view, anchor));
      const res = await getHouseholdCalendar(from, to);
      setAnswer({
        tz: res.timeZone,
        serverNow: res.serverNow,
        events: res.calendar ?? [],
        incomplete: res.incomplete === true,
        incompleteLearners: res.incompleteLearners ?? [],
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [anchor, view]);

  useEffect(() => {
    void load();
  }, [load]);

  const tz = answer?.tz;
  const days = useMemo(() => (anchor ? daysOf(view, anchor) : []), [anchor, view]);

  const learners: LearnerOption[] = useMemo(() => {
    const seen = new Map(students.map((s) => [s.studentId, s.name]));
    for (const e of answer?.events ?? []) {
      if (!seen.has(e.studentId)) seen.set(e.studentId, e.learnerName ?? 'Alumno');
    }
    return Array.from(seen, ([studentId, name]) => ({ studentId, name }));
  }, [students, answer]);

  /** Events by their day in the display zone, filtered by learner. */
  const byDay = useMemo(() => {
    const map = new Map<string, HouseholdCalendarEvent[]>();
    if (!answer) return map;
    for (const e of answer.events) {
      if (filter !== ALL_LEARNERS && e.studentId !== filter) continue;
      const key = dayKey(e.scheduledStart, answer.tz);
      map.set(key, [...(map.get(key) ?? []), e]);
    }
    return map;
  }, [answer, filter]);

  const visibleCount = days.reduce((n, d) => n + (byDay.get(d)?.length ?? 0), 0);

  const move = (step: number) => {
    if (!anchor) return;
    setSelectedDay(null);
    setAnchor(view === 'week' ? addDays(anchor, 7 * step) : addMonths(anchor, step));
  };

  const goToday = () => {
    if (!tz) return;
    setSelectedDay(null);
    setAnchor(dayKey(now.toISOString(), tz));
  };

  const changeView = (next: View) => {
    setSelectedDay(null);
    setView(next);
  };

  const periodLabel = anchor
    ? view === 'week'
      ? weekLabel(weekDays(anchor))
      : formatMonthKey(anchor)
    : '';
  const unit = view === 'week' ? 'Semana' : 'Mes';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, flex: '1 1 auto' }}>Agenda</h1>
        <Link
          to="/parent/learning"
          className="btn btn-outline btn-sm"
          style={{ textDecoration: 'none' }}
        >
          Mi aprendizaje
        </Link>
      </div>
      <p style={{ color: '#666', marginTop: 4 }}>Un calendario para toda la familia.</p>

      <LearnerFilter learners={learners} value={filter} onChange={setFilter} />

      <div
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0' }}
      >
        <div role="group" aria-label="Vista" style={{ display: 'flex', gap: 4 }}>
          {(['week', 'month'] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => changeView(v)}
            >
              {v === 'week' ? 'Semana' : 'Mes'}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => move(-1)}
          disabled={!anchor}
          aria-label={`${unit} anterior`}
        >
          ‹
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={goToday} disabled={!tz}>
          Hoy
        </button>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => move(1)}
          disabled={!anchor}
          aria-label={`${unit} siguiente`}
        >
          ›
        </button>
        {periodLabel && (
          <h2 aria-live="polite" style={{ fontSize: 17, margin: 0 }}>
            {periodLabel}
          </h2>
        )}
      </div>

      {tz && <p style={{ color: '#666', marginTop: 0, fontSize: 13 }}>{familyZoneLabel(tz)}</p>}

      {!error && !loading && answer?.incomplete && (
        <IncompleteBanner learners={answer.incompleteLearners} onRetry={load} />
      )}

      {error ? (
        <ErrorState action="cargar la agenda" error={error} onRetry={load} />
      ) : loading || !answer || !tz ? (
        <LoadingState what="la agenda" />
      ) : visibleCount === 0 && answer.incomplete ? null : visibleCount === 0 ? (
        <EmptyState
          message={view === 'week' ? 'No hay clases en esta semana.' : 'No hay clases en este mes.'}
          detail="Solo aparecen las clases que ya tienen fecha confirmada."
          action={{ label: 'Ver mi aprendizaje', to: '/parent/learning' }}
        />
      ) : view === 'week' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {days
            .filter((d) => byDay.has(d))
            .map((d) => (
              <DayEvents key={d} day={d} events={byDay.get(d) ?? []} tz={tz} now={now} />
            ))}
        </div>
      ) : (
        <>
          <MonthGrid
            anchor={anchor as string}
            byDay={byDay}
            tz={tz}
            selected={selectedDay}
            onSelect={setSelectedDay}
          />
          {selectedDay && (
            <div style={{ marginTop: 12 }}>
              {byDay.has(selectedDay) ? (
                <DayEvents
                  day={selectedDay}
                  events={byDay.get(selectedDay) ?? []}
                  tz={tz}
                  now={now}
                />
              ) : (
                <p style={{ color: '#666' }}>No hay clases el {formatDayKey(selectedDay)}.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const WEEKDAY_HEADERS = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];

/** The month at a glance; a day opens its classes below the grid. */
const MonthGrid: React.FC<{
  anchor: string;
  byDay: Map<string, HouseholdCalendarEvent[]>;
  tz: DisplayTimeZone;
  selected: string | null;
  onSelect: (day: string) => void;
}> = ({ anchor, byDay, tz, selected, onSelect }) => {
  const month = anchor.slice(0, 7);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 4,
        fontSize: 12,
      }}
    >
      {WEEKDAY_HEADERS.map((h) => (
        <div key={h} aria-hidden="true" style={{ fontWeight: 700, textAlign: 'center' }}>
          {h}
        </div>
      ))}
      {monthWeeks(anchor)
        .flat()
        .map((d) => {
          const events = byDay.get(d) ?? [];
          const inMonth = d.slice(0, 7) === month;
          const label = `${formatDayKey(d)}: ${
            events.length === 0
              ? 'sin clases'
              : events.length === 1
              ? '1 clase'
              : `${events.length} clases`
          }`;
          return (
            <button
              key={d}
              type="button"
              aria-label={label}
              aria-pressed={selected === d}
              onClick={() => onSelect(d)}
              style={{
                minHeight: 64,
                textAlign: 'left',
                verticalAlign: 'top',
                padding: 4,
                border: selected === d ? '2px solid #1c4f80' : '1px solid #dee2e6',
                borderRadius: 6,
                background: inMonth ? '#fff' : '#f8f9fa',
                color: inMonth ? '#212529' : '#868e96',
                overflow: 'hidden',
              }}
            >
              <span style={{ fontWeight: 700 }}>{Number(d.slice(8))}</span>
              {events.slice(0, 3).map((e) => (
                <span
                  key={e.bookingId}
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textDecoration: e.state === 'Cancelled' ? 'line-through' : undefined,
                  }}
                >
                  {formatTime(e.scheduledStart, tz)} {e.learnerName?.split(' ')[0] ?? ''}
                </span>
              ))}
              {events.length > 3 && (
                <span aria-hidden="true" style={{ display: 'block' }}>
                  +{events.length - 3}
                </span>
              )}
            </button>
          );
        })}
    </div>
  );
};

/** One day's classes, each with its learner, state and where it leads. */
const DayEvents: React.FC<{
  day: string;
  events: HouseholdCalendarEvent[];
  tz: DisplayTimeZone;
  now: Date;
}> = ({ day, events, tz, now }) => {
  const label = formatDayKey(day);
  return (
    <section aria-label={label}>
      <h3 style={{ fontSize: 15, margin: '8px 0', textTransform: 'capitalize' }}>{label}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {events.map((e) => (
          <EventItem key={e.bookingId} event={e} tz={tz} now={now} />
        ))}
      </ul>
    </section>
  );
};

/**
 * The calendar the server sent is partial. Said plainly, and whose, so a
 * missing class is never read as a free afternoon.
 */
const IncompleteBanner: React.FC<{ learners: string[]; onRetry: () => void }> = ({
  learners,
  onRetry,
}) => (
  <div
    role="alert"
    style={{
      border: '1px solid #e0b252',
      background: '#fff8e6',
      color: '#5c3b04',
      borderRadius: 8,
      padding: 12,
      marginBottom: 12,
      fontSize: 14,
    }}
  >
    <p style={{ margin: '0 0 8px' }}>
      {learners.length > 0
        ? `No pudimos cargar todas las clases de ${learners.join(', ')}. `
        : 'No pudimos cargar todas las clases. '}
      Es posible que falten clases en esta agenda.
    </p>
    <button type="button" onClick={onRetry}>
      Volver a intentar
    </button>
  </div>
);

const enrollmentPath = (e: HouseholdCalendarEvent) =>
  `/enrollments/${encodeURIComponent(e.enrollmentId)}`;

/**
 * Where an event leads, by its current state. The door only while the
 * server's window is open, judged on the server's clock; past classes once it
 * has happened; otherwise its enrollment.
 */
function primaryAction(
  e: HouseholdCalendarEvent,
  now: Date,
): { label: string; to: string; state?: PastClassesLinkState } {
  // The history of this event's learner, not whichever one is active.
  const pastClasses = {
    label: 'Ver clases pasadas',
    to: '/parent/past-classes',
    state: { learnerId: e.studentId },
  };
  if (e.state === 'Cancelled') return { label: 'Ver inscripción', to: enrollmentPath(e) };
  if (e.state === 'Completed' || e.state === 'NoShow') return pastClasses;
  const state = e.joinWindow ? joinStateAt(e.joinWindow, now) : undefined;
  if (state === 'OPEN' || state === 'LATE') {
    return { label: 'Entrar a la clase', to: `/sessions/${encodeURIComponent(e.bookingId)}` };
  }
  if (state === 'ENDED') return pastClasses;
  return { label: 'Ver inscripción', to: enrollmentPath(e) };
}

const EventItem: React.FC<{ event: HouseholdCalendarEvent; tz: DisplayTimeZone; now: Date }> = ({
  event: e,
  tz,
  now,
}) => {
  const cancelled = e.state === 'Cancelled';
  const action = primaryAction(e, now);
  const titleId = `event-${e.bookingId}`;
  const time = `${formatTime(e.scheduledStart, tz)}${
    e.scheduledEnd ? `–${formatTime(e.scheduledEnd, tz)}` : ''
  }`;

  return (
    <li
      aria-labelledby={titleId}
      style={{
        border: '1px solid #d1d5da',
        borderRadius: 8,
        padding: 12,
        background: cancelled ? '#f8f9fa' : '#fff',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div id={titleId} style={{ fontWeight: 700 }}>
            {e.learnerName ?? 'Alumno'} · {e.courseTitle ?? 'Curso'} · Sesión {e.seq}
          </div>
          <div
            style={{
              fontSize: 14,
              color: '#555',
              textDecoration: cancelled ? 'line-through' : undefined,
            }}
          >
            {time}
            {e.teacherName ? ` · Profesor: ${e.teacherName}` : ''}
          </div>
        </div>
        <span
          style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            fontWeight: 700,
            borderRadius: 4,
            padding: '3px 8px',
            background: cancelled ? '#eef1f5' : e.state === 'Rescheduled' ? '#fff8e6' : '#edf8e5',
            color: cancelled ? '#5a6672' : e.state === 'Rescheduled' ? '#7a4d06' : '#3f7410',
          }}
        >
          {CALENDAR_EVENT_STATE_LABEL[e.state] ?? e.state}
        </span>
      </div>

      {cancelled && (
        <p style={{ margin: '8px 0 0', fontSize: 14 }}>
          {e.cancelledWith === 'ENROLLMENT'
            ? 'Esta clase no se dará porque la inscripción se canceló.'
            : 'Esta clase se canceló y no se dará.'}
        </p>
      )}
      {e.state === 'Rescheduled' && e.proposedStart && (
        <p style={{ margin: '8px 0 0', fontSize: 14 }}>
          Se propuso moverla al <strong>{formatDayAndTime(e.proposedStart, tz)}</strong>. Mientras
          no se acepte, la clase sigue en este horario.
        </p>
      )}

      <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Link to={action.to} state={action.state}>
          {action.label}
        </Link>
        {action.to !== enrollmentPath(e) && <Link to={enrollmentPath(e)}>Ver inscripción</Link>}
      </div>
    </li>
  );
};

export default ParentSchedule;
